import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader, StatusBadge, EmptyState, ReadOnlyNote } from "@/components/Common";
import { FileSpreadsheet, UploadCloud, Sparkles, Plus, Trash2, Loader2, Pencil, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import InvoiceEmailSettings from "@/components/InvoiceEmailSettings";

const blank = () => ({ vendor: "", invoice_number: "", invoice_date: "", due_date: "", status: "draft", tax: 0, line_items: [], file_path: "" });

export default function Invoices() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [extracting, setExtracting] = useState(false);
  const [delId, setDelId] = useState(null);
  const [filter, setFilter] = useState("all");
  const fileRef = useRef();

  const reload = useCallback(() => api.get("/invoices").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  const counts = {
    all: items.length,
    review: items.filter((r) => r.status === "review").length,
    email: items.filter((r) => r.source === "email").length,
    verified: items.filter((r) => r.status === "verified").length,
  };
  const filtered = items.filter((r) => {
    if (filter === "all") return true;
    if (filter === "review") return r.status === "review";
    if (filter === "email") return r.source === "email";
    if (filter === "verified") return r.status === "verified";
    return true;
  });

  const subtotal = (form.line_items || []).reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
  const total = subtotal + (parseFloat(form.tax) || 0);

  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...blank(), ...row }); setOpen(true); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing(null);
      setForm({ ...blank(), ...data, status: "draft", line_items: data.line_items || [] });
      setOpen(true);
      toast.success("AI extracted the invoice — review and adjust below");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Extraction failed. Enter manually.");
      setForm(blank()); setEditing(null); setOpen(true);
    } finally { setExtracting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setLine = (i, key, val) => {
    const li = [...form.line_items];
    li[i] = { ...li[i], [key]: val };
    if (key === "quantity" || key === "unit_price") li[i].amount = (parseFloat(li[i].quantity) || 0) * (parseFloat(li[i].unit_price) || 0);
    setForm({ ...form, line_items: li });
  };
  const addLine = () => setForm({ ...form, line_items: [...(form.line_items || []), { description: "", ndc: "", quantity: 1, unit_price: 0, amount: 0 }] });
  const delLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, x) => x !== i) });

  const save = async () => {
    if (!form.vendor) return toast.error("Vendor is required");
    const payload = { ...form, subtotal: Math.round(subtotal * 100) / 100, total: Math.round(total * 100) / 100, tax: parseFloat(form.tax) || 0 };
    try {
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success("Invoice saved"); setOpen(false); reload();
    } catch (e) { toast.error("Save failed"); }
  };

  const remove = async () => { try { await api.delete(`/invoices/${delId}`); toast.success("Deleted"); setDelId(null); reload(); } catch (e) { toast.error("Delete failed"); } };

  return (
    <div>
      <PageHeader testid="invoices-header" title="Invoice Digitization" subtitle="Upload a photo or PDF — AI extracts the data, you verify and adjust.">
        {isAdmin ? (
          <>
            <input data-testid="invoice-file-input" ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onUpload} />
            <Button data-testid="upload-invoice-button" onClick={() => fileRef.current?.click()} disabled={extracting} className="bg-pink-700 hover:bg-pink-800 gap-1.5">
              {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} {extracting ? "Extracting…" : "Upload & Extract"}
            </Button>
            <Button data-testid="invoice-manual-button" onClick={openNew} variant="outline" className="gap-1.5"><Plus className="w-4 h-4" /> Manual</Button>
          </>
        ) : <ReadOnlyNote />}
      </PageHeader>

      {isAdmin && <InvoiceEmailSettings />}

      {isAdmin && (
        <div data-testid="upload-invoice-dropzone" onClick={() => fileRef.current?.click()}
          className="mb-6 border-2 border-dashed border-slate-200 hover:border-pink-300 rounded-xl p-8 text-center cursor-pointer bg-white transition-colors">
          <div className="w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center mx-auto mb-3"><Sparkles className="w-6 h-6 text-pink-700" /></div>
          <p className="font-heading font-semibold text-slate-700">Drop or click to upload an invoice</p>
          <p className="text-sm text-slate-400 mt-1">JPG, PNG or PDF · powered by Gemini 3.1 Pro vision</p>
        </div>
      )}

      <Card className="p-0 overflow-hidden border-slate-200">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
          {[
            { key: "all",       label: "All",         color: "slate" },
            { key: "review",    label: "Review only", color: "amber" },
            { key: "email",     label: "From email",  color: "sky"   },
            { key: "verified",  label: "Verified",    color: "emerald" },
          ].map((c) => {
            const active = filter === c.key;
            const activeCls = {
              slate:   "bg-slate-800 text-white",
              amber:   "bg-amber-500 text-white",
              sky:     "bg-sky-600 text-white",
              emerald: "bg-emerald-600 text-white",
            }[c.color];
            return (
              <button key={c.key} data-testid={`invoice-filter-${c.key}`} onClick={() => setFilter(c.key)}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                  active ? `${activeCls} border-transparent` : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}>
                {c.label}
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
                  active ? "bg-white/25" : "bg-slate-100 text-slate-500"
                }`}>{counts[c.key]}</span>
              </button>
            );
          })}
        </div>
        {filtered.length === 0 ? (
          <EmptyState icon={FileSpreadsheet}
            title={filter === "all" ? "No invoices yet" : `No invoices match "${filter}"`}
            hint={filter === "all" ? "Upload your first invoice to get started." : "Try switching filter or clearing it."} />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="invoices-table">
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Vendor</TableHead><TableHead>Invoice #</TableHead><TableHead>Date</TableHead>
                <TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead>
                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`invoice-row-${r.id}`} className="hover:bg-slate-50/60">
                    <TableCell className="font-medium text-slate-800">
                      <div className="flex items-center gap-1.5">
                        {r.vendor}
                        {r.source === "email" && (
                          <span title={`Emailed from ${r.source_sender}`} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                            <Mail className="w-3 h-3" /> email
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.invoice_number || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.invoice_date || "—"}</TableCell>
                    <TableCell className="text-sm">{(r.line_items || []).length}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold">${(r.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    {isAdmin && <TableCell className="text-right whitespace-nowrap">
                      <Button data-testid={`invoice-edit-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                      <Button data-testid={`invoice-delete-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDelId(r.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">{editing ? "Edit" : "Review"} Invoice
            {!editing && <span className="text-xs font-normal bg-pink-50 text-pink-800 px-2 py-0.5 rounded-full flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI draft — verify below</span>}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2"><Label className="text-xs text-slate-600">Vendor *</Label><Input data-testid="invoice-vendor-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-600">Invoice #</Label><Input data-testid="invoice-number-input" className="font-mono" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-600">Status</Label>
              <select data-testid="invoice-status-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                <option value="draft">Draft</option><option value="verified">Verified</option><option value="archived">Archived</option></select></div>
            <div><Label className="text-xs text-slate-600">Invoice Date</Label><Input data-testid="invoice-date-input" type="date" value={form.invoice_date || ""} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-600">Due Date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>

          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold text-slate-700">Line Items</Label>
              <Button data-testid="invoice-add-line" size="sm" variant="outline" onClick={addLine} className="h-8 gap-1"><Plus className="w-3.5 h-3.5" /> Add line</Button>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoice-line-items-table">
                <thead className="bg-slate-50 text-xs text-slate-500"><tr>
                  <th className="text-left p-2">Description</th><th className="text-left p-2">NDC</th>
                  <th className="p-2 w-16">Qty</th><th className="p-2 w-24">Unit $</th><th className="p-2 w-24">Amount</th><th className="w-8" />
                </tr></thead>
                <tbody>
                  {(form.line_items || []).map((li, i) => (
                    <tr key={li.id || `${li.ndc || "line"}-${i}`} className="border-t border-slate-100">
                      <td className="p-1"><Input value={li.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} className="h-8 border-0" /></td>
                      <td className="p-1"><Input value={li.ndc || ""} onChange={(e) => setLine(i, "ndc", e.target.value)} className="h-8 border-0 font-mono text-xs" /></td>
                      <td className="p-1"><Input type="number" value={li.quantity || ""} onChange={(e) => setLine(i, "quantity", e.target.value)} className="h-8 border-0 text-center" /></td>
                      <td className="p-1"><Input type="number" value={li.unit_price || ""} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="h-8 border-0 font-mono" /></td>
                      <td className="p-2 font-mono text-right">${(parseFloat(li.amount) || 0).toFixed(2)}</td>
                      <td className="p-1">
                        <button type="button" onClick={() => delLine(i)}><Trash2 className="w-4 h-4 text-rose-400" /></button>
                      </td>
                    </tr>
                  ))}
                  {(form.line_items || []).length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-400 text-sm">No line items — add one.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-8 mt-3 text-sm">
              <div className="text-right space-y-1">
                <div className="text-slate-500">Subtotal: <span className="font-mono text-slate-800">${subtotal.toFixed(2)}</span></div>
                <div className="flex items-center gap-2 justify-end">Tax: <Input type="number" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} className="h-8 w-24 font-mono" /></div>
                <div className="font-heading text-lg font-bold text-slate-900">Total: <span data-testid="invoice-total" className="font-mono text-pink-800">${total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="invoice-save-button" onClick={save} className="bg-pink-700 hover:bg-pink-800">Save Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* end invoice edit dialog */}

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="invoice-confirm-delete" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
